import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { twMerge } from 'tailwind-merge';
import {
  differenceInCalendarDays,
  format,
  isValid,
  parseISO,
  addDays,
} from 'date-fns';
import toast from 'react-hot-toast';
import {
  ArrowDown,
  ArrowUp,
  Building2,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  Copy,
  Diamond,
  Download,
  FileSpreadsheet,
  Filter,
  GripVertical,
  IndentDecrease,
  IndentIncrease,
  Info,
  Keyboard,
  Layers,
  Loader2,
  Maximize2,
  Minimize2,
  Network,
  Plus,
  Redo2,
  Search,
  Square,
  StickyNote,
  Trash2,
  Undo2,
  X,
  Zap,
} from 'lucide-react';
import type {
  EpcProjectMembershipRow,
  EpcProjectOrgUnit,
} from '../../../services/epcProjectService';
import {
  getWbsLevelBadgeClasses,
  normalizeWbsLevel,
  WBS_INDENT_STEP_PX,
} from './utils/epcScheduleWbsLevelStyle';
import {
  assignableMembersForOrg,
  departmentHeadForOrg,
  orgUnitKey,
  taskAssignedUserId,
  taskOrgUnitId,
} from './utils/epcScheduleDepartmentAssignment';
import {
  EPC_SCHEDULE_TASK_ENTITY,
  type EpcScheduleTaskRow,
} from '../types/scheduleTask';
import {
  parsePredecessorLinks,
  type ParsedPredecessorLink,
} from './utils/epcScheduleDependencies';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

export type EpcScheduleEditorProps = {
  tasks: EpcScheduleTaskRow[];
  onChange: (next: EpcScheduleTaskRow[]) => void;
  onSave: () => void | Promise<void>;
  saving: boolean;
  dirty: boolean;
  orgUnits: EpcProjectOrgUnit[];
  memberships: EpcProjectMembershipRow[];
  /** Optional handlers for header shortcuts (export / import). */
  onExportCsv?: () => void;
  onExportXml?: () => void;
  onImportMpp?: () => void;
  exportingXml?: boolean;
  /** When true, the editor renders as a fixed full-viewport workspace. */
  isFullView?: boolean;
  /** Toggles between embedded and full-view layouts. */
  onToggleFullView?: () => void;
};

/* ------------------------------------------------------------------ */
/*  Helpers                                                            */
/* ------------------------------------------------------------------ */

function nextTempUid(rows: EpcScheduleTaskRow[]): number {
  let min = 0;
  for (const r of rows) {
    const n = Number(r.uniqueId);
    if (Number.isFinite(n) && n < min) min = n;
  }
  return min - 1;
}

function nextVisualTaskId(rows: EpcScheduleTaskRow[]): number {
  let max = 0;
  for (const r of rows) {
    const n = Number(r.id);
    if (Number.isFinite(n) && n > max) max = n;
  }
  return max + 1;
}

function toIsoDate(raw: unknown): string {
  if (raw == null) return '';
  const s = String(raw);
  if (!s) return '';
  if (s.length >= 10 && /^\d{4}-\d{2}-\d{2}/.test(s)) return s.slice(0, 10);
  const d = parseISO(s);
  if (!isValid(d)) return '';
  return format(d, 'yyyy-MM-dd');
}

function parseDate(raw: unknown): Date | null {
  const iso = toIsoDate(raw);
  if (!iso) return null;
  const d = parseISO(iso);
  return isValid(d) ? d : null;
}

function durationDays(start: unknown, finish: unknown): number | null {
  const s = parseDate(start);
  const f = parseDate(finish);
  if (!s || !f) return null;
  return Math.max(1, differenceInCalendarDays(f, s) + 1);
}

function isTruthyFlag(v: unknown): boolean {
  return v === true || v === 'true' || v === 1 || v === '1';
}

function isSummaryFlag(t: EpcScheduleTaskRow): boolean {
  return isTruthyFlag(t.isSummary);
}

function isMilestoneFlag(t: EpcScheduleTaskRow): boolean {
  return isTruthyFlag(t.milestone);
}

/** Serialize parsed predecessors into a compact MS-Project style string. */
function formatPredecessorsMsStyle(
  links: ParsedPredecessorLink[],
  tasks: EpcScheduleTaskRow[]
): string {
  if (!links.length) return '';
  const parts: string[] = [];
  for (const lk of links) {
    // Prefer MS task id; fall back to uniqueId lookup in tasks
    let id: number | string | undefined;
    if (lk.predecessorTaskId != null && lk.predecessorTaskId !== '') {
      id = String(lk.predecessorTaskId);
    } else if (lk.predecessorUniqueId != null && lk.predecessorUniqueId !== '') {
      const found = tasks.find(
        (t) => String(t.uniqueId) === String(lk.predecessorUniqueId)
      );
      id = found?.id != null ? String(found.id) : String(lk.predecessorUniqueId);
    } else if (lk.predecessorWbs) {
      const found = tasks.find((t) => String(t.wbs) === String(lk.predecessorWbs));
      id = found?.id != null ? String(found.id) : undefined;
    }
    if (id == null || id === '') continue;
    const code = lk.linkTypeCode && lk.linkTypeCode !== 'FS' ? lk.linkTypeCode : '';
    const lag = lk.lag ? (/^[+-]/.test(lk.lag) ? lk.lag : `+${lk.lag}`) : '';
    parts.push(`${id}${code}${lag}`);
  }
  return parts.join(', ');
}

/** Parse an MPP-style predecessor string like "3FS+2d, 5SS, 7+1d" into link objects. */
function parsePredecessorsMsStyle(
  input: string,
  tasks: EpcScheduleTaskRow[]
): Array<Record<string, unknown>> {
  const out: Array<Record<string, unknown>> = [];
  if (!input || !input.trim()) return out;
  const tokens = input.split(/[,;\n]+/).map((s) => s.trim()).filter(Boolean);
  const re = /^(\d+)\s*(FS|SS|FF|SF)?\s*([+-]\s*\d+(?:\.\d+)?\s*[a-zA-Z]*)?$/i;
  for (const tok of tokens) {
    const m = re.exec(tok.replace(/\s+/g, ''));
    if (!m) continue;
    const [, idStr, codeRaw, lagRaw] = m;
    const code = (codeRaw || 'FS').toUpperCase();
    const linkId = Number(idStr);
    const found = tasks.find((t) => Number(t.id) === linkId);
    const lag = lagRaw ? lagRaw.replace(/\s+/g, '') : undefined;
    out.push({
      linkTypeCode: code,
      type: code === 'FS' ? 'FINISH_START' : code === 'SS' ? 'START_START' : code === 'FF' ? 'FINISH_FINISH' : 'START_FINISH',
      predecessorTaskId: linkId,
      predecessorUniqueId: found?.uniqueId ?? linkId,
      predecessorName: typeof found?.name === 'string' ? found.name : undefined,
      predecessorWbs: typeof found?.wbs === 'string' ? found.wbs : undefined,
      lag: lag ?? '0d',
    });
  }
  return out;
}

/** Inclusive subtree end (exclusive index). */
function subtreeEndIndex(rows: EpcScheduleTaskRow[], i: number): number {
  const L = Number(rows[i]?.outlineLevel) || 1;
  let j = i + 1;
  while (j < rows.length && (Number(rows[j]?.outlineLevel) || 1) > L) j += 1;
  return j;
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

export default function EpcScheduleEditor({
  tasks,
  onChange,
  onSave,
  saving,
  dirty,
  orgUnits,
  memberships,
  onExportCsv,
  onExportXml,
  onImportMpp,
  exportingXml = false,
  isFullView = false,
  onToggleFullView,
}: EpcScheduleEditorProps) {
  const [selection, setSelection] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [showOnlyUnassigned, setShowOnlyUnassigned] = useState(false);
  const [notesOpenFor, setNotesOpenFor] = useState<number | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [dropTarget, setDropTarget] = useState<{ index: number; pos: 'above' | 'below' } | null>(null);

  const historyRef = useRef<EpcScheduleTaskRow[][]>([]);
  const futureRef = useRef<EpcScheduleTaskRow[][]>([]);
  const tasksRef = useRef<EpcScheduleTaskRow[]>(tasks);
  tasksRef.current = tasks;

  /** Push current tasks to history then apply a mutator to get next state. */
  const applyChange = useCallback(
    (
      next: EpcScheduleTaskRow[] | ((prev: EpcScheduleTaskRow[]) => EpcScheduleTaskRow[]),
      resetFuture = true
    ) => {
      const prev = tasksRef.current;
      historyRef.current = [...historyRef.current.slice(-49), prev];
      if (resetFuture) futureRef.current = [];
      const computed = typeof next === 'function'
        ? (next as (p: EpcScheduleTaskRow[]) => EpcScheduleTaskRow[])(prev)
        : next;
      onChange(computed);
    },
    [onChange]
  );

  const undo = useCallback(() => {
    const last = historyRef.current.pop();
    if (!last) {
      toast('Nothing to undo', { icon: 'ℹ️' });
      return;
    }
    futureRef.current.push(tasksRef.current);
    onChange(last);
  }, [onChange]);

  const redo = useCallback(() => {
    const next = futureRef.current.pop();
    if (!next) {
      toast('Nothing to redo', { icon: 'ℹ️' });
      return;
    }
    historyRef.current.push(tasksRef.current);
    onChange(next);
  }, [onChange]);

  /* -------------------------------- filtering -------------------------------- */

  const filteredIndices = useMemo(() => {
    const q = search.trim().toLowerCase();
    const out: number[] = [];
    for (let i = 0; i < tasks.length; i += 1) {
      const t = tasks[i];
      if (deptFilter && taskOrgUnitId(t) !== deptFilter) continue;
      if (showOnlyUnassigned && taskAssignedUserId(t)) continue;
      if (q) {
        const name = String(t.name ?? '').toLowerCase();
        const wbs = String(t.wbs ?? '').toLowerCase();
        const id = String(t.id ?? '').toLowerCase();
        if (!name.includes(q) && !wbs.includes(q) && !id.includes(q)) continue;
      }
      out.push(i);
    }
    return out;
  }, [tasks, search, deptFilter, showOnlyUnassigned]);

  /* -------------------------------- stats ------------------------------------ */

  const stats = useMemo(() => {
    let summaryCount = 0;
    let milestoneCount = 0;
    let percentSum = 0;
    let percentN = 0;
    let minStart: Date | null = null;
    let maxFinish: Date | null = null;
    let unassigned = 0;
    let noDept = 0;
    for (const t of tasks) {
      if (isSummaryFlag(t)) summaryCount += 1;
      if (isMilestoneFlag(t)) milestoneCount += 1;
      const pc = Number(t.percentComplete);
      if (Number.isFinite(pc)) {
        percentSum += Math.max(0, Math.min(100, pc));
        percentN += 1;
      }
      const s = parseDate(t.start);
      const f = parseDate(t.finish);
      if (s && (!minStart || s < minStart)) minStart = s;
      if (f && (!maxFinish || f > maxFinish)) maxFinish = f;
      if (!taskAssignedUserId(t)) unassigned += 1;
      if (!taskOrgUnitId(t)) noDept += 1;
    }
    const avgPc = percentN > 0 ? Math.round(percentSum / percentN) : 0;
    const spanDays =
      minStart && maxFinish ? differenceInCalendarDays(maxFinish, minStart) + 1 : 0;
    return {
      total: tasks.length,
      summaryCount,
      milestoneCount,
      avgPc,
      spanDays,
      minStart,
      maxFinish,
      unassigned,
      noDept,
    };
  }, [tasks]);

  /* -------------------------------- mutations -------------------------------- */

  const updateRow = useCallback(
    (idx: number, patch: Partial<EpcScheduleTaskRow>) => {
      applyChange((prev) => prev.map((t, i) => (i === idx ? { ...t, ...patch } : t)));
    },
    [applyChange]
  );

  const patchDepartment = useCallback(
    (idx: number, nextOrg: string) => {
      const oid = nextOrg.trim() || undefined;
      const cur = taskAssignedUserId(tasks[idx]!);
      const patch: Partial<EpcScheduleTaskRow> = { epcOrgUnitId: oid };
      if (cur && oid) {
        const allowed = new Set(
          assignableMembersForOrg(memberships, oid).map((m) => m.userId)
        );
        if (!allowed.has(cur)) patch.assignedUserId = undefined;
      } else if (!oid) {
        patch.assignedUserId = undefined;
      }
      updateRow(idx, patch);
    },
    [tasks, memberships, updateRow]
  );

  const addRow = useCallback(() => {
    applyChange((prev) => [
      ...prev,
      {
        uniqueId: nextTempUid(prev),
        id: nextVisualTaskId(prev),
        taskEntityType: EPC_SCHEDULE_TASK_ENTITY,
        name: 'New task',
        outlineLevel: 1,
        start: '',
        finish: '',
        percentComplete: 0,
        isSummary: false,
        predecessors: [],
        successors: [],
        epcOrgUnitId: undefined,
        assignedUserId: undefined,
      },
    ]);
  }, [applyChange]);

  const insertRelative = useCallback(
    (pos: 'above' | 'below', refIdx: number) => {
      applyChange((prev) => {
        const ref = prev[refIdx];
        const baseLevel = Number(ref?.outlineLevel) || 1;
        const newTask: EpcScheduleTaskRow = {
          uniqueId: nextTempUid(prev),
          id: nextVisualTaskId(prev),
          taskEntityType: EPC_SCHEDULE_TASK_ENTITY,
          name: 'New task',
          outlineLevel: baseLevel,
          start: '',
          finish: '',
          percentComplete: 0,
          isSummary: false,
          predecessors: [],
          successors: [],
          epcOrgUnitId: ref?.epcOrgUnitId,
          assignedUserId: undefined,
        };
        const insertAt = pos === 'above' ? refIdx : refIdx + 1;
        const next = [...prev];
        next.splice(insertAt, 0, newTask);
        return next;
      });
    },
    [applyChange]
  );

  const duplicateRows = useCallback(
    (indices: number[]) => {
      if (!indices.length) return;
      applyChange((prev) => {
        const sorted = [...indices].sort((a, b) => a - b);
        let shift = 0;
        const result = [...prev];
        for (const idx of sorted) {
          const original = result[idx + shift];
          if (!original) continue;
          const clone: EpcScheduleTaskRow = {
            ...original,
            uniqueId: nextTempUid(result),
            id: nextVisualTaskId(result),
            name: `${original.name ?? 'Task'} (copy)`,
            predecessors: [],
            successors: [],
          };
          result.splice(idx + shift + 1, 0, clone);
          shift += 1;
        }
        return result;
      });
    },
    [applyChange]
  );

  const removeRows = useCallback(
    (indices: number[]) => {
      if (!indices.length) return;
      const set = new Set(indices);
      applyChange((prev) => prev.filter((_, i) => !set.has(i)));
      setSelection(new Set());
    },
    [applyChange]
  );

  const shiftSubtree = useCallback(
    (startIdx: number, delta: number) => {
      applyChange((prev) => {
        const end = subtreeEndIndex(prev, startIdx);
        return prev.map((t, i) => {
          if (i < startIdx || i >= end) return t;
          const cur = Number(t.outlineLevel) || 1;
          const next = Math.max(1, Math.min(12, cur + delta));
          return { ...t, outlineLevel: next };
        });
      });
    },
    [applyChange]
  );

  const indentSelected = useCallback(
    (dir: 1 | -1) => {
      if (!selection.size) {
        toast('Select rows to indent/outdent', { icon: '↹' });
        return;
      }
      const ordered = [...selection].sort((a, b) => a - b);
      // Apply per subtree, but avoid double-applying nested subtrees.
      const covered = new Set<number>();
      for (const idx of ordered) {
        if (covered.has(idx)) continue;
        shiftSubtree(idx, dir);
        const end = subtreeEndIndex(tasksRef.current, idx);
        for (let j = idx; j < end; j += 1) covered.add(j);
      }
    },
    [selection, shiftSubtree]
  );

  const moveRows = useCallback(
    (direction: 'up' | 'down') => {
      if (!selection.size) return;
      applyChange((prev) => {
        const sel = [...selection].sort((a, b) => a - b);
        const next = [...prev];
        if (direction === 'up') {
          for (const idx of sel) {
            if (idx <= 0) continue;
            [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
          }
        } else {
          for (let k = sel.length - 1; k >= 0; k -= 1) {
            const idx = sel[k]!;
            if (idx >= next.length - 1) continue;
            [next[idx + 1], next[idx]] = [next[idx], next[idx + 1]];
          }
        }
        return next;
      });
      const deltaMap = direction === 'up' ? -1 : 1;
      setSelection(new Set([...selection].map((i) => i + deltaMap)));
    },
    [selection, applyChange]
  );

  const toggleFlag = useCallback(
    (field: 'isSummary' | 'milestone') => {
      if (!selection.size) {
        toast('Select rows to toggle', { icon: '☑️' });
        return;
      }
      applyChange((prev) => {
        const allSet = [...selection].every((i) => isTruthyFlag(prev[i]?.[field]));
        return prev.map((t, i) => {
          if (!selection.has(i)) return t;
          return { ...t, [field]: !allSet };
        });
      });
    },
    [selection, applyChange]
  );

  const bulkSetDepartment = useCallback(
    (orgId: string) => {
      if (!selection.size) return;
      applyChange((prev) =>
        prev.map((t, i) => {
          if (!selection.has(i)) return t;
          const oid = orgId || undefined;
          const cur = taskAssignedUserId(t);
          const patch: Partial<EpcScheduleTaskRow> = { epcOrgUnitId: oid };
          if (cur && oid) {
            const allowed = new Set(
              assignableMembersForOrg(memberships, oid).map((m) => m.userId)
            );
            if (!allowed.has(cur)) patch.assignedUserId = undefined;
          } else if (!oid) {
            patch.assignedUserId = undefined;
          }
          return { ...t, ...patch };
        })
      );
    },
    [selection, memberships, applyChange]
  );

  /* -------------------------------- drag & drop ------------------------------ */

  const handleDragStart = (e: DragEvent<HTMLTableRowElement>, idx: number) => {
    setDragIndex(idx);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(idx));
  };

  const handleDragOver = (e: DragEvent<HTMLTableRowElement>, idx: number) => {
    e.preventDefault();
    if (dragIndex == null || dragIndex === idx) return;
    const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
    const pos: 'above' | 'below' = e.clientY < rect.top + rect.height / 2 ? 'above' : 'below';
    setDropTarget((prev) =>
      prev && prev.index === idx && prev.pos === pos ? prev : { index: idx, pos }
    );
  };

  const handleDrop = (e: DragEvent<HTMLTableRowElement>, idx: number) => {
    e.preventDefault();
    const from = dragIndex;
    const target = dropTarget;
    setDragIndex(null);
    setDropTarget(null);
    if (from == null || target == null || from === idx) return;
    applyChange((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      if (!moved) return prev;
      let insertAt = target.index;
      if (from < target.index) insertAt -= 1;
      if (target.pos === 'below') insertAt += 1;
      insertAt = Math.max(0, Math.min(next.length, insertAt));
      next.splice(insertAt, 0, moved);
      return next;
    });
  };

  /* -------------------------------- keyboard --------------------------------- */

  const tableRef = useRef<HTMLDivElement>(null);

  const onTableKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      const tgt = e.target as HTMLElement;
      const inEditable =
        tgt.tagName === 'INPUT' ||
        tgt.tagName === 'TEXTAREA' ||
        tgt.tagName === 'SELECT' ||
        tgt.isContentEditable;

      const meta = e.ctrlKey || e.metaKey;

      if (meta && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
        return;
      }
      if (meta && (e.key.toLowerCase() === 'y' || (e.key.toLowerCase() === 'z' && e.shiftKey))) {
        e.preventDefault();
        redo();
        return;
      }
      if (meta && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (dirty && !saving) void onSave();
        return;
      }
      if (meta && e.key.toLowerCase() === 'd' && selection.size) {
        e.preventDefault();
        duplicateRows([...selection]);
        return;
      }

      if (inEditable) return; // Let typing flow naturally

      if (e.key === 'Tab') {
        if (selection.size) {
          e.preventDefault();
          indentSelected(e.shiftKey ? -1 : 1);
        }
      } else if (e.key === 'Delete' && selection.size) {
        e.preventDefault();
        removeRows([...selection]);
      }
    },
    [undo, redo, onSave, dirty, saving, selection, duplicateRows, indentSelected, removeRows]
  );

  /* -------------------------------- selection helpers ----------------------- */

  const toggleSelectOne = (idx: number, evt: { shiftKey?: boolean; metaKey?: boolean; ctrlKey?: boolean }) => {
    const multi = evt.metaKey || evt.ctrlKey || evt.shiftKey;
    setSelection((prev) => {
      const next = new Set(prev);
      if (!multi) {
        if (prev.has(idx) && prev.size === 1) {
          next.clear();
        } else {
          next.clear();
          next.add(idx);
        }
      } else if (next.has(idx)) {
        next.delete(idx);
      } else {
        next.add(idx);
      }
      return next;
    });
  };

  const allVisibleSelected =
    filteredIndices.length > 0 && filteredIndices.every((i) => selection.has(i));

  const toggleSelectAllVisible = () => {
    setSelection((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const i of filteredIndices) next.delete(i);
      } else {
        for (const i of filteredIndices) next.add(i);
      }
      return next;
    });
  };

  /* -------------------------------- date helpers ---------------------------- */

  const onStartChange = (idx: number, value: string) => {
    const cur = tasks[idx];
    const iso = value || '';
    const patch: Partial<EpcScheduleTaskRow> = { start: iso };
    const finishCur = parseDate(cur?.finish);
    const startNew = parseDate(iso);
    // If start moves past current finish, keep duration: push finish to start+1
    if (startNew && finishCur && startNew > finishCur) {
      patch.finish = format(addDays(startNew, 1), 'yyyy-MM-dd');
    }
    updateRow(idx, patch);
  };

  const onFinishChange = (idx: number, value: string) => {
    const cur = tasks[idx];
    const iso = value || '';
    const patch: Partial<EpcScheduleTaskRow> = { finish: iso };
    const startCur = parseDate(cur?.start);
    const finishNew = parseDate(iso);
    if (startCur && finishNew && finishNew < startCur) {
      patch.start = format(addDays(finishNew, -1), 'yyyy-MM-dd');
    }
    updateRow(idx, patch);
  };

  const onDurationChange = (idx: number, days: number) => {
    const cur = tasks[idx];
    const startCur = parseDate(cur?.start);
    if (!startCur) {
      toast.error('Set start date first');
      return;
    }
    const d = Math.max(1, Math.round(days));
    updateRow(idx, {
      finish: format(addDays(startCur, d - 1), 'yyyy-MM-dd'),
      duration: `${d}d`,
    });
  };

  /* -------------------------------- mini gantt geometry --------------------- */

  const ganttScale = useMemo(() => {
    if (!stats.minStart || !stats.maxFinish) return null;
    const start = stats.minStart.getTime();
    const end = stats.maxFinish.getTime();
    const range = Math.max(1, end - start);
    return { start, end, range };
  }, [stats.minStart, stats.maxFinish]);

  /* -------------------------------- render --------------------------------- */

  const selectedCount = selection.size;
  const overallSpanLabel =
    stats.minStart && stats.maxFinish
      ? `${format(stats.minStart, 'd MMM yy')} → ${format(stats.maxFinish, 'd MMM yy')}`
      : '—';

  /* Esc exits full view (only when active and a toggle is available). */
  useEffect(() => {
    if (!isFullView || !onToggleFullView) return;
    const handler = (ev: KeyboardEvent) => {
      if (ev.key !== 'Escape') return;
      const tgt = ev.target as HTMLElement | null;
      const tag = tgt?.tagName?.toLowerCase();
      const editing =
        tag === 'input' || tag === 'textarea' || tag === 'select' || (tgt && tgt.isContentEditable);
      if (editing) return; /* let the field consume Esc first */
      ev.preventDefault();
      onToggleFullView();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isFullView, onToggleFullView]);

  /* Lock body scroll while full view is active so background page can't scroll under us. */
  useEffect(() => {
    if (!isFullView) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isFullView]);

  return (
    <motion.div
      initial={isFullView ? false : { opacity: 0, y: 6 }}
      animate={isFullView ? false : { opacity: 1, y: 0 }}
      className={twMerge(
        isFullView
          ? 'fixed inset-0 z-[120] flex flex-col overflow-auto bg-white'
          : 'relative overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-xl ring-1 ring-slate-100',
      )}
    >
      {/* Hero header */}
      <div
        className="relative overflow-hidden border-b border-slate-200/90 px-5 py-4"
        style={{
          background:
            'linear-gradient(135deg, rgba(13,148,136,0.09) 0%, rgba(16,185,129,0.05) 55%, #ffffff 100%)',
        }}
      >
        <div className="pointer-events-none absolute -right-20 -top-20 h-56 w-56 rounded-full bg-teal-400/15 blur-3xl" />
        <div className="pointer-events-none absolute -left-12 top-10 h-40 w-40 rounded-full bg-emerald-400/10 blur-3xl" />

        <div className="relative flex flex-wrap items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <span className="flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-teal-600 to-emerald-600 text-white shadow-lg shadow-teal-600/30 ring-2 ring-white/80">
              <Layers className="h-5 w-5" strokeWidth={2.25} />
            </span>
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                  Task studio — Edit schedule
                </h2>
                {dirty ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-amber-900 ring-1 ring-amber-200">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500 animate-pulse" />
                    Unsaved
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-800 ring-1 ring-emerald-200">
                    Saved
                  </span>
                )}
              </div>
              <p className="mt-0.5 max-w-2xl text-[12px] leading-snug text-slate-600">
                Plan like MS Project — indent/outdent tasks, set dates, percent complete,
                predecessors, departments, assignees. Import .mpp, edit here, then export as
                .mpp for Microsoft Project.
              </p>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-1.5">
            <button
              type="button"
              onClick={() => setShowHelp((s) => !s)}
              className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white/90 px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              title="Keyboard shortcuts"
            >
              <Keyboard className="h-3.5 w-3.5" />
              Shortcuts
            </button>
            {onImportMpp ? (
              <button
                type="button"
                onClick={onImportMpp}
                className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50/90 px-2.5 py-1.5 text-[11px] font-semibold text-teal-900 shadow-sm transition hover:bg-teal-100/90"
                title="Upload .mpp, URL import, import history"
              >
                <Network className="h-3.5 w-3.5" />
                Import MPP
              </button>
            ) : null}
            {onExportCsv ? (
              <button
                type="button"
                onClick={onExportCsv}
                className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                title="Download tasks as CSV"
              >
                <FileSpreadsheet className="h-3.5 w-3.5" />
                CSV
              </button>
            ) : null}
            {onExportXml ? (
              <button
                type="button"
                disabled={exportingXml}
                onClick={onExportXml}
                className="inline-flex items-center gap-1.5 rounded-lg border border-teal-200 bg-teal-50/90 px-2.5 py-1.5 text-[11px] font-semibold text-teal-900 shadow-sm transition hover:bg-teal-100/90 disabled:opacity-50"
                title="Download for Microsoft Project (.mpp when licensed, .xml fallback — open in Project and Save As .mpp)"
              >
                {exportingXml ? (
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                ) : (
                  <Download className="h-3.5 w-3.5" />
                )}
                Export for MS Project
              </button>
            ) : null}
            {onToggleFullView ? (
              <button
                type="button"
                onClick={onToggleFullView}
                className={twMerge(
                  'inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold shadow-sm transition',
                  isFullView
                    ? 'border-slate-700 bg-slate-800 text-white hover:bg-slate-900'
                    : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50',
                )}
                title={isFullView ? 'Exit full view (Esc)' : 'Open in full view'}
              >
                {isFullView ? (
                  <Minimize2 className="h-3.5 w-3.5" />
                ) : (
                  <Maximize2 className="h-3.5 w-3.5" />
                )}
                {isFullView ? 'Exit full view' : 'Full view'}
              </button>
            ) : null}
            <motion.button
              type="button"
              whileTap={{ scale: 0.97 }}
              disabled={saving || !dirty}
              onClick={() => void onSave()}
              className="inline-flex items-center gap-2 rounded-xl bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-2 text-[12px] font-bold uppercase tracking-wide text-white shadow-lg shadow-teal-600/30 transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-45"
              title="Save as a new schedule version (Ctrl+S)"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Zap className="h-4 w-4" />}
              {saving ? 'Saving…' : 'Save version'}
            </motion.button>
          </div>
        </div>

        {/* Stats strip */}
        <div className="relative mt-3 flex flex-wrap gap-2">
          <StatChip label="Tasks" value={String(stats.total)} />
          <StatChip label="Summaries" value={String(stats.summaryCount)} />
          <StatChip
            label="Milestones"
            value={String(stats.milestoneCount)}
            icon={<Diamond className="h-3 w-3 text-violet-600" />}
          />
          <StatChip label="Avg % complete" value={`${stats.avgPc}%`} tone="emerald" />
          <StatChip label="Window" value={overallSpanLabel} />
          {stats.spanDays > 0 ? <StatChip label="Days" value={String(stats.spanDays)} /> : null}
          {stats.unassigned > 0 ? (
            <StatChip label="Unassigned" value={String(stats.unassigned)} tone="amber" />
          ) : null}
          {stats.noDept > 0 ? (
            <StatChip label="No dept" value={String(stats.noDept)} tone="amber" />
          ) : null}
        </div>
      </div>

      {/* Help flyout */}
      <AnimatePresence>
        {showHelp ? (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-b border-slate-200 bg-gradient-to-r from-slate-50 to-white"
          >
            <div className="grid gap-3 px-5 py-3 text-[11px] text-slate-700 sm:grid-cols-2 lg:grid-cols-4">
              <ShortcutHint keys={['Tab']} desc="Indent selected rows" />
              <ShortcutHint keys={['Shift', 'Tab']} desc="Outdent selected rows" />
              <ShortcutHint keys={['Ctrl', 'Z']} desc="Undo" />
              <ShortcutHint keys={['Ctrl', 'Shift', 'Z']} desc="Redo" />
              <ShortcutHint keys={['Ctrl', 'D']} desc="Duplicate selected rows" />
              <ShortcutHint keys={['Del']} desc="Delete selected rows" />
              <ShortcutHint keys={['Ctrl', 'S']} desc="Save new version" />
              <ShortcutHint keys={['Click']} desc="Select · Ctrl/⌘-Click to multi-select" />
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Action toolbar */}
      <div className="flex flex-wrap items-center gap-1.5 border-b border-slate-200/80 bg-slate-50/60 px-3 py-2">
        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1 py-0.5 shadow-sm">
          <ToolbarBtn onClick={undo} icon={<Undo2 className="h-3.5 w-3.5" />} label="Undo" />
          <ToolbarBtn onClick={redo} icon={<Redo2 className="h-3.5 w-3.5" />} label="Redo" />
        </div>

        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1 py-0.5 shadow-sm">
          <ToolbarBtn
            onClick={addRow}
            icon={<Plus className="h-3.5 w-3.5 text-teal-600" />}
            label="Add task"
          />
          <ToolbarBtn
            disabled={selectedCount !== 1}
            onClick={() => {
              const i = [...selection][0];
              if (i != null) insertRelative('above', i);
            }}
            icon={<ArrowUp className="h-3.5 w-3.5" />}
            label="Insert above"
          />
          <ToolbarBtn
            disabled={selectedCount !== 1}
            onClick={() => {
              const i = [...selection][0];
              if (i != null) insertRelative('below', i);
            }}
            icon={<ArrowDown className="h-3.5 w-3.5" />}
            label="Insert below"
          />
          <ToolbarBtn
            disabled={selectedCount === 0}
            onClick={() => duplicateRows([...selection])}
            icon={<Copy className="h-3.5 w-3.5" />}
            label="Duplicate"
          />
        </div>

        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1 py-0.5 shadow-sm">
          <ToolbarBtn
            disabled={selectedCount === 0}
            onClick={() => indentSelected(1)}
            icon={<IndentIncrease className="h-3.5 w-3.5" />}
            label="Indent"
          />
          <ToolbarBtn
            disabled={selectedCount === 0}
            onClick={() => indentSelected(-1)}
            icon={<IndentDecrease className="h-3.5 w-3.5" />}
            label="Outdent"
          />
          <ToolbarBtn
            disabled={selectedCount === 0}
            onClick={() => moveRows('up')}
            icon={<ArrowUp className="h-3.5 w-3.5" />}
            label="Move up"
          />
          <ToolbarBtn
            disabled={selectedCount === 0}
            onClick={() => moveRows('down')}
            icon={<ArrowDown className="h-3.5 w-3.5" />}
            label="Move down"
          />
        </div>

        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1 py-0.5 shadow-sm">
          <ToolbarBtn
            disabled={selectedCount === 0}
            onClick={() => toggleFlag('isSummary')}
            icon={<Layers className="h-3.5 w-3.5" />}
            label="Summary"
          />
          <ToolbarBtn
            disabled={selectedCount === 0}
            onClick={() => toggleFlag('milestone')}
            icon={<Diamond className="h-3.5 w-3.5 text-violet-600" />}
            label="Milestone"
          />
        </div>

        <div className="flex items-center gap-1 rounded-lg border border-slate-200 bg-white px-1 py-0.5 shadow-sm">
          <select
            value=""
            onChange={(e) => {
              if (!e.target.value) return;
              bulkSetDepartment(e.target.value === '__none__' ? '' : e.target.value);
              e.currentTarget.selectedIndex = 0;
            }}
            disabled={selectedCount === 0}
            className="cursor-pointer appearance-none rounded-md bg-transparent px-2 py-1 text-[11px] font-semibold text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
            title="Bulk set department for selected rows"
          >
            <option value="">Set dept…</option>
            <option value="__none__">— None —</option>
            {orgUnits
              .filter((o) => !o.inactive)
              .map((o) => (
                <option key={orgUnitKey(o)} value={orgUnitKey(o)}>
                  {o.name}
                </option>
              ))}
          </select>
        </div>

        <ToolbarBtn
          disabled={selectedCount === 0}
          onClick={() => removeRows([...selection])}
          icon={<Trash2 className="h-3.5 w-3.5 text-rose-600" />}
          label={`Delete${selectedCount ? ` (${selectedCount})` : ''}`}
          danger
        />

        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          <div className="relative">
            <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Find task, WBS or ID…"
              className="h-8 w-[14rem] rounded-lg border border-slate-200 bg-white pl-7 pr-2 text-[11px] text-slate-900 placeholder:text-slate-400 focus:border-teal-400 focus:ring-2 focus:ring-teal-500/15 outline-none"
            />
          </div>
          <select
            value={deptFilter}
            onChange={(e) => setDeptFilter(e.target.value)}
            className="h-8 rounded-lg border border-slate-200 bg-white px-2 text-[11px] font-medium text-slate-700 focus:border-teal-400"
            title="Filter by department"
          >
            <option value="">All depts</option>
            {orgUnits.map((o) => (
              <option key={orgUnitKey(o)} value={orgUnitKey(o)}>
                {o.name}
              </option>
            ))}
          </select>
          <label className="inline-flex h-8 cursor-pointer select-none items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2 text-[11px] font-medium text-slate-700 hover:bg-slate-50">
            <input
              type="checkbox"
              checked={showOnlyUnassigned}
              onChange={(e) => setShowOnlyUnassigned(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300 text-teal-600"
            />
            <Filter className="h-3 w-3" />
            Unassigned
          </label>
        </div>
      </div>

      {/* Selection banner */}
      <AnimatePresence>
        {selectedCount > 0 ? (
          <motion.div
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            className="flex items-center justify-between gap-2 border-b border-teal-100/80 bg-gradient-to-r from-teal-50/80 to-white px-4 py-1.5 text-[11px] font-medium text-teal-900"
          >
            <span>
              <strong className="font-bold">{selectedCount}</strong> row
              {selectedCount === 1 ? '' : 's'} selected · Tab/Shift+Tab to indent, Del to remove,
              Ctrl+D to duplicate.
            </span>
            <button
              type="button"
              onClick={() => setSelection(new Set())}
              className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-[10px] font-semibold text-teal-800 hover:bg-teal-100"
            >
              <X className="h-3 w-3" />
              Clear
            </button>
          </motion.div>
        ) : null}
      </AnimatePresence>

      {/* Table */}
      <div
        ref={tableRef}
        tabIndex={0}
        onKeyDown={onTableKeyDown}
        className="relative max-h-[min(68vh,640px)] overflow-auto outline-none focus-visible:ring-2 focus-visible:ring-teal-500/20"
      >
        <table className="min-w-full border-collapse text-left text-[13px]">
          <thead className="sticky top-0 z-10 bg-gradient-to-b from-slate-100 to-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-600 shadow-sm">
            <tr>
              <th className="w-10 px-2 py-2 text-center">
                <button
                  type="button"
                  onClick={toggleSelectAllVisible}
                  className="inline-flex items-center justify-center rounded p-0.5 hover:bg-slate-200/60"
                  title={allVisibleSelected ? 'Deselect all' : 'Select all visible'}
                >
                  {allVisibleSelected ? (
                    <CheckSquare className="h-4 w-4 text-teal-600" />
                  ) : (
                    <Square className="h-4 w-4 text-slate-400" />
                  )}
                </button>
              </th>
              <th className="w-10 px-1 py-2 text-center" title="Drag to reorder">
                <span className="sr-only">Drag</span>
              </th>
              <th className="w-14 px-2 py-2" title="MS Project Task ID">
                ID
              </th>
              <th className="min-w-[16rem] px-2 py-2">Task name</th>
              <th className="w-24 px-2 py-2 text-center" title="Outline level / WBS">
                WBS
              </th>
              <th className="w-20 px-2 py-2" title="Duration in days (derived)">
                Dur (d)
              </th>
              <th className="w-36 px-2 py-2">Start</th>
              <th className="w-36 px-2 py-2">Finish</th>
              <th className="w-28 px-2 py-2" title="% Complete">
                Progress
              </th>
              <th className="min-w-[9rem] px-2 py-2" title='Predecessors — "3FS+2d, 5SS"'>
                Predecessors
              </th>
              <th className="min-w-[10rem] px-2 py-2">Department</th>
              <th className="min-w-[10rem] px-2 py-2">Assignee</th>
              {ganttScale ? (
                <th className="min-w-[10rem] px-2 py-2" title="Mini Gantt preview">
                  Bar
                </th>
              ) : null}
              <th className="w-16 px-2 py-2 text-right">
                <span className="sr-only">Actions</span>
              </th>
            </tr>
          </thead>

          <tbody>
            {filteredIndices.length === 0 ? (
              <tr>
                <td colSpan={ganttScale ? 14 : 13} className="px-4 py-12 text-center text-slate-500">
                  {tasks.length === 0 ? (
                    <div className="flex flex-col items-center gap-3">
                      <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-400">
                        <Layers className="h-6 w-6" />
                      </span>
                      <p className="text-sm font-semibold text-slate-700">No tasks yet</p>
                      <p className="max-w-md text-xs text-slate-500">
                        Add tasks manually or import a Microsoft Project (.mpp) file. Each row
                        you create here becomes part of the saved schedule version.
                      </p>
                      <div className="flex flex-wrap items-center justify-center gap-2 pt-1">
                        <button
                          type="button"
                          onClick={addRow}
                          className="inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-teal-700"
                        >
                          <Plus className="h-3.5 w-3.5" />
                          Add first task
                        </button>
                        {onImportMpp ? (
                          <button
                            type="button"
                            onClick={onImportMpp}
                            className="inline-flex items-center gap-2 rounded-xl border border-teal-200 bg-teal-50 px-4 py-2 text-xs font-semibold text-teal-800 hover:bg-teal-100"
                          >
                            <Network className="h-3.5 w-3.5" />
                            Import MPP
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm">No tasks match the current filters.</p>
                  )}
                </td>
              </tr>
            ) : (
              filteredIndices.map((idx) => {
                const row = tasks[idx]!;
                const wbsL = normalizeWbsLevel(row.outlineLevel);
                const oid = taskOrgUnitId(row);
                const head = departmentHeadForOrg(memberships, oid);
                const assignPool = assignableMembersForOrg(memberships, oid);
                const selected = selection.has(idx);
                const isSummary = isSummaryFlag(row);
                const isMilestone = isMilestoneFlag(row);
                const pc = Math.max(0, Math.min(100, Number(row.percentComplete) || 0));
                const durCalc = durationDays(row.start, row.finish);
                const predText = formatPredecessorsMsStyle(
                  parsePredecessorLinks(row.predecessors),
                  tasks
                );
                const isDropAbove =
                  dropTarget?.index === idx && dropTarget?.pos === 'above';
                const isDropBelow =
                  dropTarget?.index === idx && dropTarget?.pos === 'below';

                return (
                  <tr
                    key={idx}
                    draggable
                    onDragStart={(e) => handleDragStart(e, idx)}
                    onDragOver={(e) => handleDragOver(e, idx)}
                    onDrop={(e) => handleDrop(e, idx)}
                    onDragEnd={() => {
                      setDragIndex(null);
                      setDropTarget(null);
                    }}
                    className={twMerge(
                      'group relative border-b border-slate-100 align-top transition-colors',
                      selected
                        ? 'bg-teal-50/60 ring-2 ring-inset ring-teal-500/50'
                        : 'hover:bg-slate-50/80',
                      isSummary ? 'bg-amber-50/40' : '',
                      isMilestone ? 'bg-violet-50/30' : '',
                      isDropAbove ? 'shadow-[inset_0_3px_0_0_#0d9488]' : '',
                      isDropBelow ? 'shadow-[inset_0_-3px_0_0_#0d9488]' : '',
                      dragIndex === idx ? 'opacity-50' : ''
                    )}
                  >
                    {/* checkbox */}
                    <td className="w-10 px-2 py-1.5 text-center align-middle">
                      <button
                        type="button"
                        onClick={(e) => toggleSelectOne(idx, e)}
                        className="inline-flex items-center justify-center rounded p-0.5 hover:bg-slate-200/60"
                        aria-label={selected ? 'Deselect row' : 'Select row'}
                      >
                        {selected ? (
                          <CheckSquare className="h-4 w-4 text-teal-600" />
                        ) : (
                          <Square className="h-4 w-4 text-slate-400" />
                        )}
                      </button>
                    </td>
                    {/* drag handle */}
                    <td className="w-10 px-1 py-1.5 text-center align-middle">
                      <span
                        className="inline-flex cursor-grab items-center justify-center rounded text-slate-300 hover:text-slate-600 active:cursor-grabbing"
                        title="Drag to reorder"
                      >
                        <GripVertical className="h-4 w-4" />
                      </span>
                    </td>
                    {/* id */}
                    <td className="w-14 px-2 py-1.5 align-middle font-mono text-[11px] text-slate-500 tabular-nums">
                      {row.id != null && row.id !== '' ? String(row.id) : `#${idx + 1}`}
                    </td>

                    {/* name */}
                    <td className="min-w-[16rem] px-2 py-1.5 align-middle">
                      <div
                        className="flex min-w-0 items-center gap-1"
                        style={{ paddingLeft: (wbsL - 1) * WBS_INDENT_STEP_PX }}
                      >
                        {isSummary ? (
                          <ChevronDown
                            className="h-3.5 w-3.5 flex-shrink-0 text-amber-600"
                            aria-label="Summary task"
                          />
                        ) : wbsL > 1 ? (
                          <ChevronRight className="h-3.5 w-3.5 flex-shrink-0 text-slate-300" />
                        ) : null}
                        {isMilestone ? (
                          <Diamond
                            className="h-3.5 w-3.5 flex-shrink-0 fill-violet-500 text-violet-600"
                            aria-label="Milestone"
                          />
                        ) : null}
                        <input
                          value={String(row.name ?? '')}
                          onChange={(e) => updateRow(idx, { name: e.target.value })}
                          className={twMerge(
                            'min-w-0 flex-1 rounded-md border border-transparent bg-transparent px-1.5 py-1 text-[13px] text-slate-900 focus:border-teal-400 focus:bg-white focus:ring-2 focus:ring-teal-500/15 outline-none',
                            isSummary ? 'font-bold' : '',
                            isMilestone ? 'italic font-semibold text-violet-900' : ''
                          )}
                          placeholder="Task name…"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            setNotesOpenFor((cur) => (cur === idx ? null : idx))
                          }
                          className={twMerge(
                            'flex-shrink-0 rounded-md p-1 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700',
                            typeof row.notes === 'string' && row.notes.trim()
                              ? 'text-teal-600'
                              : '',
                            notesOpenFor === idx ? 'bg-slate-100 text-teal-700' : ''
                          )}
                          aria-label="Notes"
                          title={
                            typeof row.notes === 'string' && row.notes.trim()
                              ? 'Has notes — click to edit'
                              : 'Add notes'
                          }
                        >
                          <StickyNote className="h-3.5 w-3.5" />
                        </button>
                      </div>
                      {notesOpenFor === idx ? (
                        <textarea
                          value={String(row.notes ?? '')}
                          onChange={(e) => updateRow(idx, { notes: e.target.value })}
                          placeholder="Add notes or context…"
                          rows={2}
                          className="mt-1 w-full rounded-md border border-slate-200 bg-slate-50/70 px-2 py-1.5 text-[11px] text-slate-800 focus:border-teal-400 focus:bg-white focus:ring-2 focus:ring-teal-500/15 outline-none"
                        />
                      ) : null}
                    </td>

                    {/* WBS / outline */}
                    <td className="w-24 px-2 py-1.5 align-middle">
                      <div className="flex items-center justify-center gap-1">
                        <button
                          type="button"
                          onClick={() =>
                            updateRow(idx, {
                              outlineLevel: Math.max(1, (Number(row.outlineLevel) || 1) - 1),
                            })
                          }
                          className="rounded p-0.5 text-slate-400 hover:bg-slate-200/60 hover:text-slate-700"
                          aria-label="Outdent (decrease level)"
                          title="Decrease level"
                        >
                          <IndentDecrease className="h-3 w-3" />
                        </button>
                        <span
                          className={getWbsLevelBadgeClasses(wbsL)}
                          title={`Outline level ${wbsL}`}
                        >
                          {wbsL}
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            updateRow(idx, {
                              outlineLevel: Math.min(12, (Number(row.outlineLevel) || 1) + 1),
                            })
                          }
                          className="rounded p-0.5 text-slate-400 hover:bg-slate-200/60 hover:text-slate-700"
                          aria-label="Indent (increase level)"
                          title="Increase level"
                        >
                          <IndentIncrease className="h-3 w-3" />
                        </button>
                      </div>
                    </td>

                    {/* Duration */}
                    <td className="w-20 px-2 py-1.5 align-middle">
                      <input
                        type="number"
                        min={1}
                        value={durCalc ?? ''}
                        onChange={(e) => onDurationChange(idx, Number(e.target.value))}
                        placeholder="—"
                        disabled={!parseDate(row.start)}
                        className="w-full rounded-md border border-slate-200 bg-white px-1.5 py-1 text-center text-[11px] font-mono tabular-nums text-slate-800 focus:border-teal-400 focus:ring-2 focus:ring-teal-500/15 outline-none disabled:bg-slate-50 disabled:text-slate-400"
                        title={
                          parseDate(row.start)
                            ? 'Duration in days — editing adjusts Finish'
                            : 'Set Start date first'
                        }
                      />
                    </td>

                    {/* Start */}
                    <td className="w-36 px-2 py-1.5 align-middle">
                      <input
                        type="date"
                        value={toIsoDate(row.start)}
                        onChange={(e) => onStartChange(idx, e.target.value)}
                        className="w-full rounded-md border border-slate-200 bg-white px-1.5 py-1 font-mono text-[11px] text-slate-800 focus:border-teal-400 focus:ring-2 focus:ring-teal-500/15 outline-none"
                      />
                    </td>

                    {/* Finish */}
                    <td className="w-36 px-2 py-1.5 align-middle">
                      <input
                        type="date"
                        value={toIsoDate(row.finish)}
                        onChange={(e) => onFinishChange(idx, e.target.value)}
                        className="w-full rounded-md border border-slate-200 bg-white px-1.5 py-1 font-mono text-[11px] text-slate-800 focus:border-teal-400 focus:ring-2 focus:ring-teal-500/15 outline-none"
                      />
                    </td>

                    {/* % complete */}
                    <td className="w-28 px-2 py-1.5 align-middle">
                      <div className="flex items-center gap-1.5">
                        <div className="relative h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
                          <div
                            className="h-full rounded-full bg-gradient-to-r from-teal-500 to-emerald-500 transition-[width] duration-200"
                            style={{ width: `${pc}%` }}
                          />
                        </div>
                        <input
                          type="number"
                          min={0}
                          max={100}
                          value={pc}
                          onChange={(e) =>
                            updateRow(idx, {
                              percentComplete: Math.max(
                                0,
                                Math.min(100, Number(e.target.value) || 0)
                              ),
                            })
                          }
                          className="w-11 rounded-md border border-slate-200 bg-white px-1 py-0.5 text-right text-[10px] font-mono tabular-nums text-slate-700 focus:border-teal-400 focus:ring-2 focus:ring-teal-500/15 outline-none"
                        />
                      </div>
                      {Number.isFinite(Number(row['fieldPercentComplete'])) && (
                        <p
                          className="mt-0.5 text-right text-[9.5px] font-mono tabular-nums text-emerald-700"
                          title="Derived from field quantity claims and actuals — read-only overlay, reconcile vs the MPP % above"
                        >
                          Field {Math.round(Number(row['fieldPercentComplete']))}%
                        </p>
                      )}
                    </td>

                    {/* Predecessors */}
                    <td className="min-w-[9rem] px-2 py-1.5 align-middle">
                      <input
                        defaultValue={predText}
                        key={predText}
                        onBlur={(e) => {
                          const parsed = parsePredecessorsMsStyle(e.target.value, tasks);
                          updateRow(idx, { predecessors: parsed });
                        }}
                        placeholder="e.g. 3FS+2d, 5"
                        className="w-full rounded-md border border-slate-200 bg-white px-1.5 py-1 font-mono text-[11px] text-slate-800 focus:border-teal-400 focus:ring-2 focus:ring-teal-500/15 outline-none"
                        title='Format: "<id><type><+/-lag>" — e.g. "3FS+2d, 5SS, 7"'
                      />
                    </td>

                    {/* Department */}
                    <td className="min-w-[10rem] px-2 py-1.5 align-middle">
                      <select
                        value={oid ?? ''}
                        onChange={(e) => patchDepartment(idx, e.target.value)}
                        className="w-full rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-800 focus:border-teal-400"
                      >
                        <option value="">— None —</option>
                        {orgUnits
                          .filter((u) => !u.inactive)
                          .map((u) => (
                            <option key={orgUnitKey(u)} value={orgUnitKey(u)}>
                              {u.name}
                            </option>
                          ))}
                      </select>
                      {oid && head ? (
                        <p className="mt-0.5 flex items-center gap-1 text-[9.5px] leading-tight text-slate-500">
                          <Building2 className="h-2.5 w-2.5" />
                          Head: {head.userName || head.userId}
                        </p>
                      ) : oid && !head ? (
                        <p className="mt-0.5 flex items-center gap-1 text-[9.5px] text-amber-700">
                          <Info className="h-2.5 w-2.5" />
                          No head in team
                        </p>
                      ) : null}
                    </td>

                    {/* Assignee */}
                    <td className="min-w-[10rem] px-2 py-1.5 align-middle">
                      <select
                        value={taskAssignedUserId(row) ?? ''}
                        onChange={(e) => {
                          const v = e.target.value.trim();
                          updateRow(idx, { assignedUserId: v || undefined });
                        }}
                        disabled={!oid}
                        className="w-full rounded-md border border-slate-200 bg-white px-1.5 py-1 text-[11px] text-slate-800 focus:border-teal-400 disabled:bg-slate-50 disabled:text-slate-400"
                      >
                        <option value="">— Unassigned —</option>
                        {assignPool.map((m) => (
                          <option key={m.userId} value={m.userId}>
                            {(m.userName || m.userId) +
                              (m.role === 'manager' ? ' · Manager' : ' · Site eng.')}
                          </option>
                        ))}
                      </select>
                    </td>

                    {/* Mini Gantt */}
                    {ganttScale ? (
                      <td className="min-w-[10rem] px-2 py-1.5 align-middle">
                        <MiniGanttBar
                          start={parseDate(row.start)}
                          finish={parseDate(row.finish)}
                          scale={ganttScale}
                          pc={pc}
                          isSummary={isSummary}
                          isMilestone={isMilestone}
                        />
                      </td>
                    ) : null}

                    {/* Row actions */}
                    <td className="w-16 px-2 py-1.5 text-right align-middle">
                      <div className="flex items-center justify-end gap-0.5 opacity-50 transition group-hover:opacity-100">
                        <button
                          type="button"
                          onClick={() => duplicateRows([idx])}
                          className="rounded-md p-1 text-slate-400 hover:bg-slate-200/60 hover:text-slate-700"
                          aria-label="Duplicate"
                          title="Duplicate row (Ctrl+D)"
                        >
                          <Copy className="h-3.5 w-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => removeRows([idx])}
                          className="rounded-md p-1 text-rose-500 hover:bg-rose-50 hover:text-rose-700"
                          aria-label="Delete row"
                          title="Delete row (Del)"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-slate-200/90 bg-slate-50/70 px-4 py-2 text-[11px] text-slate-600">
        <span>
          Showing <strong className="text-slate-900">{filteredIndices.length}</strong> of{' '}
          <strong className="text-slate-900">{tasks.length}</strong> tasks
        </span>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={addRow}
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-50"
          >
            <Plus className="h-3.5 w-3.5 text-teal-600" />
            Add task
          </button>
          <motion.button
            type="button"
            whileTap={{ scale: 0.97 }}
            disabled={saving || !dirty}
            onClick={() => void onSave()}
            className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-teal-600 to-emerald-600 px-4 py-1.5 text-[11px] font-bold uppercase tracking-wide text-white shadow-sm transition hover:brightness-110 disabled:opacity-45"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Zap className="h-3.5 w-3.5" />}
            {saving ? 'Saving…' : 'Save version'}
          </motion.button>
        </div>
      </div>
    </motion.div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function StatChip({
  label,
  value,
  icon,
  tone = 'slate',
}: {
  label: string;
  value: string;
  icon?: React.ReactNode;
  tone?: 'slate' | 'emerald' | 'amber';
}) {
  const toneCls = {
    slate: 'bg-white/85 ring-slate-200/80 text-slate-800',
    emerald: 'bg-emerald-50/85 ring-emerald-200/80 text-emerald-900',
    amber: 'bg-amber-50/85 ring-amber-200/85 text-amber-900',
  }[tone];
  return (
    <span
      className={twMerge(
        'inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[10px] font-semibold shadow-sm ring-1 backdrop-blur',
        toneCls
      )}
    >
      {icon}
      <span className="uppercase tracking-wider text-[9px] opacity-70">{label}</span>
      <span className="font-bold tabular-nums">{value}</span>
    </span>
  );
}

function ShortcutHint({ keys, desc }: { keys: string[]; desc: string }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex items-center gap-1">
        {keys.map((k, i) => (
          <kbd
            key={i}
            className="inline-flex min-w-[1.5rem] items-center justify-center rounded-md border border-slate-300 bg-white px-1.5 py-0.5 text-[10px] font-mono font-semibold text-slate-700 shadow-sm"
          >
            {k}
          </kbd>
        ))}
      </span>
      <span>{desc}</span>
    </div>
  );
}

function ToolbarBtn({
  onClick,
  icon,
  label,
  disabled = false,
  danger = false,
}: {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  disabled?: boolean;
  danger?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      className={twMerge(
        'inline-flex items-center gap-1 rounded-md px-2 py-1 text-[11px] font-semibold transition disabled:cursor-not-allowed disabled:opacity-40',
        danger
          ? 'text-rose-700 hover:bg-rose-50'
          : 'text-slate-700 hover:bg-slate-100'
      )}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );
}

function MiniGanttBar({
  start,
  finish,
  scale,
  pc,
  isSummary,
  isMilestone,
}: {
  start: Date | null;
  finish: Date | null;
  scale: { start: number; end: number; range: number };
  pc: number;
  isSummary: boolean;
  isMilestone: boolean;
}) {
  if (!start && !finish) {
    return <span className="text-[10px] text-slate-300">—</span>;
  }
  const s = (start ?? finish)!.getTime();
  const f = (finish ?? start)!.getTime();
  const leftPct = Math.max(0, Math.min(100, ((s - scale.start) / scale.range) * 100));
  const widthPct = Math.max(1.5, Math.min(100 - leftPct, ((f - s) / scale.range) * 100));

  if (isMilestone) {
    return (
      <div className="relative h-4 w-full">
        <div
          className="absolute top-1/2 -translate-y-1/2"
          style={{ left: `calc(${leftPct}% - 6px)` }}
          aria-label="Milestone marker"
        >
          <Diamond className="h-3 w-3 fill-violet-500 text-violet-600" />
        </div>
      </div>
    );
  }

  return (
    <div className="relative h-3 w-full overflow-hidden rounded-sm bg-slate-100/60">
      <div
        className={twMerge(
          'absolute inset-y-0 rounded-sm shadow-sm',
          isSummary
            ? 'bg-gradient-to-b from-slate-700 to-slate-900'
            : 'bg-gradient-to-b from-teal-400 to-teal-600'
        )}
        style={{ left: `${leftPct}%`, width: `${widthPct}%` }}
      >
        {pc > 0 && !isSummary ? (
          <div
            className="h-full bg-gradient-to-b from-emerald-600 to-emerald-800"
            style={{ width: `${pc}%` }}
          />
        ) : null}
      </div>
    </div>
  );
}
